import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux'; // Cambiar connect por hooks
import ReactGA from 'react-ga';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import smartlookClient from 'smartlook-client';

import UserSession from '../core/session';
import { teamsListActions } from '../core/teams-list';
import { Header } from './components/common';
import { userActions } from '../core/users';
import routes from "../routes";
import { systemCheckActions } from '../core/systemCheck/actions';
import { AccountNotFound } from './components/account-notFound/AccountNotFound';
import { PopupAcceptTerms } from './components/popups/popupAcceptTerms';
import { PopupBrokenPasswordPolicies } from './components/popups/popupBrokenPasswordPolicies';
import LoadingIndicator from './components/loading-indicator';
import { SYSTEM_ID_LOGIN_POPUPS } from '../core/constants';
import { GenericPopup } from './components/popups/GenericPopup';
import { getSystemById, systemActions } from '../core/system';
import { setNavigation } from "../navigationService";

import '../assets/css/styles.scss';
import '../assets/css/App.scss';

const App = React.memo(() => {
  const headerRef = useRef();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const user = useSelector(state => state.user);
  const systemCheck = useSelector(state => state.systemCheck);
  
  const isAuthenticated = UserSession.getAuthenticated() || !!user?.id;
  const checked = user.checked;
  const thinkingTalentsOn = systemCheck?.data?.data?.thinking_talents_on;
  const mindPattersOn = systemCheck?.data?.data?.mind_patterns_on;
  const memberSearchOn = user?.account?.community_enabled;
  const teamsOn = systemCheck?.data?.data?.teams_on;
  const login_popups = useSelector(state => getSystemById(state, SYSTEM_ID_LOGIN_POPUPS)?.items);

  const [openPopupAcceptTerms, setOpenPopupAcceptTerms] = useState(false);
  const [openPopupBrokenPasswordPolicies, setOpenPopupBrokenPasswordPolicies] = useState(false);
  const [currentPopupIndex, setCurrentPopupIndex] = useState(0);

  const toggle = () => setOpenPopupAcceptTerms(!openPopupAcceptTerms);
  const togglePopupBrokenPasswordPolicie = () => setOpenPopupBrokenPasswordPolicies(!openPopupBrokenPasswordPolicies);
  const nextPopup = () => setCurrentPopupIndex(currentPopupIndex + 1);

  useEffect(() => {
    dispatch(userActions.getProfile());
    dispatch(systemCheckActions.loadSystemCheck());
  }, [dispatch]);

  useEffect(() => {
    setNavigation(navigate, location);
  }, [navigate, location]);

  useEffect(() => {
    if (!user.id && location.pathname === '/welcome') {
      dispatch(systemActions.loadSystem(SYSTEM_ID_LOGIN_POPUPS));
    }

    user && user.accept_terms !== 1
      ? setOpenPopupAcceptTerms(true)
      : setOpenPopupAcceptTerms(false);

    user && user.must_change_pwd === 1
      ? setOpenPopupBrokenPasswordPolicies(true)
      : setOpenPopupBrokenPasswordPolicies(false);
  }, [user, location.pathname, dispatch]);

  const initializeGa = () => {
    if (systemCheck?.data?.data?.ga_id && location.pathname) {
      ReactGA.initialize(systemCheck.data.data.ga_id);
      ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
    }
  };

  const initializeSmartlookCliente = () => {
    if (systemCheck?.data?.data?.smartlook_id) {
      smartlookClient.init(systemCheck.data.data.smartlook_id);
    }
  };

  useEffect(() => {
    if (!window.ga && location.pathname) {
      initializeGa();
    }
  }, [systemCheck?.data?.data, location.pathname]);

  useEffect(() => {
    if (window.ga && location?.pathname) {
      ReactGA.set({ page: location.pathname });
      ReactGA.pageview(location.pathname);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!smartlookClient.initialized()) {
      initializeSmartlookCliente();
    } else {
      console.log('smart listo');
    }
  }, [systemCheck?.data?.data]);

  const public_pages = ['login', 'not-found', 'signup', 'welcome', 'confirmRegister', 'contactUs', 'privacy', 'account-information-sent', 'forgotRegister', 'resetPassword', 'resetPasswordDone'];

  const searchParams = new URLSearchParams(location.search);
  const systemCheckData = systemCheck && systemCheck.data && systemCheck.data.data;
  const invId = searchParams.get('inv-id');

  let register_allowed_invitation = systemCheckData && systemCheckData.register_allowed_invitation && systemCheckData.register_allowed_invitation === invId;
  let allow_register = systemCheckData && systemCheckData.allow_register;

  if (!checked) return <LoadingIndicator />;

  return (
    <>
      {user.id && location.pathname === '/welcome' && <Navigate to="/home" replace />}
      {systemCheck.data.message === "Unauthorized" && <AccountNotFound />}
      {systemCheck.isPending && <LoadingIndicator />}
      {user.isPending && <LoadingIndicator />}
      {user.id && <PopupAcceptTerms isOpen={openPopupAcceptTerms} toggle={toggle} />}
      {user.id && (
        <PopupBrokenPasswordPolicies
          isOpen={user.must_change_pwd && openPopupBrokenPasswordPolicies}
          toggle={togglePopupBrokenPasswordPolicie}
        />
      )}
      {!user.id && login_popups?.size > 0 && location.pathname === '/welcome' &&
        login_popups?.map((popup, index) => (
          <GenericPopup
            key={popup.id}
            id={popup.id}
            isOpen={currentPopupIndex === index}
            toggle={nextPopup}
            text={popup.text}
            show_command_button={popup.show_command_button}
            command_button_text={popup.command_button_text}
            command_button_link={popup.command_button_link}
            new_tab={popup.open_link_In_a_new_tab}
          />
        ))
      }

      {systemCheck.data.message !== `Unauthorized` && !systemCheck.isPending && (
        <div
          className={`App page-${location.pathname.split('/')[1]}`}
          onClick={() => headerRef?.current?.globalToggleMenu()}
        >
          <main role="main">
            <Routes>
              {routes.map((route, index) => {
                const auth_required = !public_pages.includes(route.name);

                if (auth_required) {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        isAuthenticated ? (
                          <div className="py-md-3 d-md-flex align-items-md-stretch minvh-100">
                            {user && isAuthenticated && (
                              <div id="sidebarMenu" className="sidebar">
                                <Header
                                  user={user}
                                  showThinkingTalents={thinkingTalentsOn}
                                  showMindPatterns={mindPattersOn}
                                  showTeams={teamsOn}
                                  showMemberSearch={memberSearchOn}
                                  loadTeams={() => dispatch(teamsListActions.loadTeams())}
                                />
                              </div>
                            )}
                            <route.component />
                          </div>
                        ) : (
                          <Navigate to="/welcome" replace />
                        )
                      }
                    />
                  );
                } else {
                  return (
                    <Route
                      key={index}
                      path={route.path}
                      element={
                        (route.name === 'signup' &&
                          ((!allow_register && !register_allowed_invitation) || isAuthenticated)) ? (
                          <Navigate to="/welcome" replace />
                        ) : route.name === 'privacy' || route.name === 'contactUs' ? (
                          <route.component
                            checked={checked}
                            authenticated={isAuthenticated}
                            user={user}
                          />
                        ) : isAuthenticated ? (
                          <Navigate to="/home" replace />
                        ) : (
                          <route.component
                            checked={checked}
                            authenticated={isAuthenticated}
                            user={user}
                          />
                        )
                      }
                    />
                  );
                }
              })}
            </Routes>
          </main>
        </div>
      )}
    </>
  );
});

export default App;